/**
 * "You're on Free now." (J) - shown once per expiry episode, per account, per
 * device. Server-side acknowledgment sync across devices is out of scope.
 *
 * An episode is an authoritative individual snapshot that has fallen back to
 * Free after a paid plan whose end date is known and in the past. The key is
 * `${paidTier}:${endedAt}`, so a later subscription that lapses again (with a
 * new end date) is a new episode, while relaunches of the same one are not.
 *
 * Never shown: while a purchase/sync is in flight, for unknown status, for
 * employer plans, for admin grants, or when the end date is unknown (the
 * screen's "{tier} ended on {date}" line would have to guess).
 *
 * The tier that ended comes from `snapshot.lapsedFromTier` (derived server
 * side from the last provider product, because the sync writes `plan: 'free'`
 * at expiry so `legacyPlan` is 'free' by then); a paid `legacyPlan` is the
 * fallback for older snapshots. Episodes older than LAPSE_NOTICE_MAX_AGE_MS
 * are never shown: the notice is for a recent change, not a months-old one
 * surfacing on first launch after a release.
 */
import { normalizeLegacyPlanTier } from '@tabbio/utils/subscription/catalog';
import type { SubscriptionSnapshot } from '@tabbio/types';
import { createSecureStoreKey } from '../secure-store-key';

export const LAPSE_ACK_STORAGE_PREFIX = 'tabbio_lapse_ack_v1';
/** How long after the end date J is still worth showing (30 days). */
export const LAPSE_NOTICE_MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;

export interface LapseEpisode {
  key: string;
  tier: 'plus' | 'pro';
  endedAt: string;
}

const UNKNOWN_STATUSES = new Set(['', 'unknown']);

export function getLapseEpisode(
  snapshot: SubscriptionSnapshot | null | undefined,
  now = Date.now(),
): LapseEpisode | null {
  if (!snapshot || snapshot.planSet !== 'individual') return null;
  if (snapshot.tier !== 'free') return null;
  if (!snapshot.status || UNKNOWN_STATUSES.has(snapshot.status.toLowerCase())) {
    return null;
  }
  if (snapshot.source === 'admin_grant') return null;
  const previous =
    snapshot.lapsedFromTier ?? normalizeLegacyPlanTier(snapshot.legacyPlan);
  if (previous !== 'plus' && previous !== 'pro') return null;
  const raw = snapshot.renewsAt;
  if (!raw || !Number.isFinite(Date.parse(raw))) return null;
  const ended = Date.parse(raw);
  if (ended > now) return null;
  if (now - ended > LAPSE_NOTICE_MAX_AGE_MS) return null;
  // Normalised so "…Z" and "….000Z" spellings of one instant are one episode.
  const endedAt = new Date(raw).toISOString();
  return { key: `${previous}:${endedAt}`, tier: previous, endedAt };
}

export function getLapseEpisodeKey(
  snapshot: SubscriptionSnapshot | null | undefined,
  now = Date.now(),
) {
  return getLapseEpisode(snapshot, now)?.key ?? null;
}

/** Account-scoped storage key; code points keep punctuation-only ids distinct. */
export function lapseAckStorageKey(accountId: string) {
  const encoded = Array.from(accountId)
    .map((char) => char.codePointAt(0)!.toString(16))
    .join('-');
  return createSecureStoreKey(LAPSE_ACK_STORAGE_PREFIX, encoded);
}

export function shouldShowLapseNotice(input: {
  snapshot: SubscriptionSnapshot | null | undefined;
  acknowledgedKey: string | null;
  /** A purchase, restore or sync in flight: the Free snapshot may be stale. */
  isSyncing: boolean;
  now?: number;
}): LapseEpisode | null {
  if (input.isSyncing) return null;
  const episode = getLapseEpisode(input.snapshot, input.now);
  if (!episode || episode.key === input.acknowledgedKey) return null;
  return episode;
}

export interface LapseAckStorage {
  read: (key: string) => string | null;
  write: (key: string, value: string) => void;
}

/** Reads/writes the last acknowledged episode key for an account. */
export function createLapseAckStore(storage: LapseAckStorage) {
  return {
    get(accountId: string) {
      try {
        return storage.read(lapseAckStorageKey(accountId));
      } catch {
        return null;
      }
    },
    acknowledge(accountId: string, episodeKey: string) {
      try {
        storage.write(lapseAckStorageKey(accountId), episodeKey);
      } catch {
        // Storage can be unavailable (private mode); worst case J shows again.
      }
    },
  };
}
